import { useQuery } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LoadingState, ErrorState } from "@/components/states";
import { useSupabaseClient } from "@/lib/supabase";
import { fetchUserActivityDetail } from "../api/adminMonitoring";

interface UserActivityDrawerProps {
  userId: string | null;
  onClose: () => void;
}

function formatUsd(n: number | undefined | null): string {
  const v = Number(n ?? 0);
  if (v === 0) return "$0";
  if (v < 0.01) return `$${v.toFixed(6)}`;
  return `$${v.toFixed(4)}`;
}

function formatNumber(n: number | undefined | null): string {
  return Number(n ?? 0).toLocaleString("pt-BR");
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "Nunca";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function UserActivityDrawer({ userId, onClose }: UserActivityDrawerProps) {
  const { client, isReady } = useSupabaseClient();
  const open = userId !== null;

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "user_activity_detail", userId],
    queryFn: () => fetchUserActivityDetail(client, userId!, 30),
    enabled: open && isReady,
  });

  const maxDailyCost = data?.daily?.length
    ? Math.max(...data.daily.map((d) => Number(d.cost_usd)), 0.000001)
    : 1;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {data?.profile?.display_name || data?.profile?.email || "Atividade do usuário"}
          </SheetTitle>
          {data?.profile?.email && (
            <SheetDescription className="font-mono text-xs">
              {data.profile.email}
            </SheetDescription>
          )}
        </SheetHeader>

        {isLoading && <LoadingState />}
        {error && <ErrorState error={error} />}
        {data && (
          <div className="mt-5 space-y-6">
            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border/50 bg-card p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Último login</p>
                <p className="mt-1 text-xs font-mono">{formatDateTime(data.last_login_at)}</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-card p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Último uso</p>
                <p className="mt-1 text-xs font-mono">{formatDateTime(data.last_use_at)}</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-card p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Eventos (30d)</p>
                <p className="mt-1 text-sm font-semibold">{formatNumber(data.totals.events)}</p>
              </div>
              <div className="rounded-lg border border-border/50 bg-card p-3">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Custo (30d)</p>
                <p className="mt-1 text-sm font-semibold text-emerald-400">
                  {formatUsd(data.totals.cost_usd)}
                </p>
              </div>
              <div className="rounded-lg border border-border/50 bg-card p-3 col-span-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Tokens (30d)</p>
                <p className="mt-1 text-xs font-mono">
                  in {formatNumber(data.totals.tokens_in)} · out {formatNumber(data.totals.tokens_out)}
                </p>
              </div>
            </div>

            {/* Daily bars */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Atividade diária (30d)
              </h3>
              {data.daily.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem eventos no período.</p>
              ) : (
                <div className="flex items-end gap-1 h-24 rounded-lg border border-border/40 bg-card/40 p-3">
                  {data.daily.map((d) => {
                    const h = (Number(d.cost_usd) / maxDailyCost) * 100;
                    return (
                      <div
                        key={d.day}
                        title={`${d.day}: ${formatUsd(d.cost_usd)} · ${d.events} eventos`}
                        className="flex-1 rounded-sm bg-primary/70 hover:bg-primary transition-colors"
                        style={{ height: `${Math.max(h, d.events > 0 ? 4 : 1)}%`, minHeight: "1px" }}
                      />
                    );
                  })}
                </div>
              )}
            </section>

            {/* By feature */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Por feature (30d)
              </h3>
              {data.by_feature.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem features registradas.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.by_feature.map((f) => (
                    <div
                      key={f.feature}
                      className="flex items-center justify-between rounded-md border border-border/40 bg-card/60 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-mono text-foreground truncate">{f.feature}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {f.events} eventos · {formatNumber(f.tokens)} tokens
                        </p>
                      </div>
                      <p className="text-xs font-mono text-emerald-400 shrink-0 ml-3">
                        {formatUsd(f.cost_usd)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* By model */}
            <section>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Por modelo (30d)
              </h3>
              {data.by_model.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem chamadas registradas.</p>
              ) : (
                <div className="space-y-1.5">
                  {data.by_model.map((m) => (
                    <div
                      key={`${m.provider}-${m.model_id}`}
                      className="flex items-center justify-between rounded-md border border-border/40 bg-card/60 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="text-xs font-mono text-foreground truncate">{m.model_id}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {m.provider} · {m.events} chamadas · in {formatNumber(m.tokens_in)} / out{" "}
                          {formatNumber(m.tokens_out)}
                        </p>
                      </div>
                      <p className="text-xs font-mono text-emerald-400 shrink-0 ml-3">
                        {formatUsd(m.cost_usd)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
